// Session Timeout Hook
// Tracks idle time and triggers warning/logout based on admin-configured timeout

import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuthStore } from '../stores/auth-store';
import { trpc } from '../lib/trpc';
import type { ActivityTracker } from './useActivityTracker';
import { tryRefreshToken } from './useTokenRefresh';

interface SessionTimeoutState {
  showWarning: boolean;
  remainingSeconds: number;
  stayLoggedIn: () => void;
  dismissWarning: () => void;
}

interface TenantSettingsContainer {
  settings?: Record<string, unknown>;
}

const CHECK_INTERVAL_MS = 1_000; // Check every second when warning is shown
const IDLE_CHECK_INTERVAL_MS = 10_000; // Check idle every 10 seconds normally

function readNumericSetting(
  settings: Record<string, unknown> | undefined,
  key: 'sessionTimeoutMinutes' | 'sessionWarningMinutes',
): number | null {
  const value = settings?.[key];
  return typeof value === 'number' ? value : null;
}

export function useSessionTimeout(activityTracker: ActivityTracker): SessionTimeoutState {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const { isAuthenticated, logout } = useAuthStore();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const idleCheckRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Fetch tenant settings
  const tenantQuery = trpc.tenant.getCurrent.useQuery(undefined, {
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  const tenantSettings = (tenantQuery.data as TenantSettingsContainer | undefined)?.settings;

  const sessionTimeoutMinutes = readNumericSetting(tenantSettings, 'sessionTimeoutMinutes');
  const sessionWarningMinutes = readNumericSetting(tenantSettings, 'sessionWarningMinutes') ?? 3;

  const stayLoggedIn = useCallback(async () => {
    setShowWarning(false);
    setRemainingSeconds(0);
    activityTracker.resetActivity();
    await tryRefreshToken();
  }, [activityTracker]);

  const dismissWarning = useCallback(() => {
    setShowWarning(false);
    setRemainingSeconds(0);
    activityTracker.resetActivity();
  }, [activityTracker]);

  // Idle checking - runs periodically to detect when warning should appear
  useEffect(() => {
    if (!isAuthenticated || sessionTimeoutMinutes === null) {
      return;
    }

    const warningThresholdMinutes = sessionTimeoutMinutes - sessionWarningMinutes;

    const checkIdle = () => {
      const idleMs = Date.now() - activityTracker.getLastActivity();
      const idleMinutes = idleMs / 60_000;

      if (idleMinutes >= sessionTimeoutMinutes) {
        // Past timeout - log out immediately
        logout();
        return;
      }

      if (idleMinutes >= warningThresholdMinutes && !showWarning) {
        // Enter warning phase
        const remainingMs = (sessionTimeoutMinutes * 60_000) - idleMs;
        setRemainingSeconds(Math.max(Math.ceil(remainingMs / 1000), 0));
        setShowWarning(true);
      }
    };

    idleCheckRef.current = setInterval(checkIdle, IDLE_CHECK_INTERVAL_MS);
    // Run immediately
    checkIdle();

    return () => {
      if (idleCheckRef.current) {
        clearInterval(idleCheckRef.current);
        idleCheckRef.current = null;
      }
    };
  }, [isAuthenticated, sessionTimeoutMinutes, sessionWarningMinutes, activityTracker, logout, showWarning]);

  // Countdown timer - runs every second when warning is shown
  useEffect(() => {
    if (!showWarning || sessionTimeoutMinutes === null) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      const idleMs = Date.now() - activityTracker.getLastActivity();
      const remainingMs = (sessionTimeoutMinutes * 60_000) - idleMs;
      const remaining = Math.max(Math.ceil(remainingMs / 1000), 0);

      setRemainingSeconds(remaining);

      if (remaining <= 0) {
        logout();
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [showWarning, sessionTimeoutMinutes, activityTracker, logout]);

  return {
    showWarning: showWarning && isAuthenticated,
    remainingSeconds,
    stayLoggedIn,
    dismissWarning,
  };
}
